import Menubar from "@/components/common/Menubar";
import NeuralNetworkBackground from "@/components/common/NeuralNetworkBackground";
import ActivateAccount from "@/pages/activateAccount/ActivateAccount";
import Dashboard from "@/pages/dashboard/Dashboard";
import Users from "@/pages/users/Users";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import {
  ACTIVATE_ACCOUNT_PAGE,
  ADD_APP_PAGE,
  BILLING_PAGE,
  DASHBOARD_PAGE,
  FORGOT_PASSWORD_PAGE,
  INTEGRATIONS_PAGE,
  LOGIN_PAGE,
  MANAGE_APPS_PAGE,
  MANAGE_USERS_PAGE,
  MFA_CHALLENGE_PAGE,
  NEW_USER_LOGIN_PAGE,
  REQUEST_INVITE_PAGE,
  RESET_PASSPHRASE_PAGE,
  SINGLE_WEBAPP_PAGE,
  UPDATE_PASSPHRASE_PAGE,
  USER_PAGE,
  VULNERABILITY_PAGE,
} from "../constants/definitions";
import Applications from "../pages/applications/Applications";
import Integrations from "../pages/integrations/Integrations";
import ChangePassword from "../pages/auth/ChangePassword";
import ForgotPassword from "../pages/auth/ForgotPassword";
import RequestInvite from "../pages/auth/RequestInvite";
import Login from "../pages/auth/Login";
import MfaChallenge from "../pages/auth/MfaChallenge";
import ResetPassword from "../pages/auth/ResetPassword";
import NewApplication from "../pages/newApplication/NewApplication";
import User from "../pages/user/User";
import BillingPage from "../pages/billing/Billing";
import Vulnerability from "../pages/vulnerability/Vulnerability";
import WebApplication from "../pages/webApplication/WebApplication";
import { AppRoutesProps } from "../types/interfaces";
import { PrivateRoute } from "./PrivateRoute";
import { RoleGate } from "./RoleGate";
import { OwnerGate } from "./OwnerGate";
import { useSessionWatchdog } from "../utils/useSessionWatchdog";

const AUTH_PATHS = [LOGIN_PAGE, NEW_USER_LOGIN_PAGE, REQUEST_INVITE_PAGE, FORGOT_PASSWORD_PAGE, RESET_PASSPHRASE_PAGE, ACTIVATE_ACCOUNT_PAGE, UPDATE_PASSPHRASE_PAGE, MFA_CHALLENGE_PAGE];

export function AppRoutes(props: AppRoutesProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthPage = AUTH_PATHS.includes(location.pathname);

  // Watch the Cognito session in the background. If the refresh
  // token dies (TTL exceeded, server-side revocation, tenant
  // disabled), this signs the user out and routes back to the
  // login page. Gated on isAuthPage so it stays a no-op across the
  // whole auth flow — including the /activate challenge, where
  // isAuthenticated() is true but no session exists yet, so probing
  // would falsely log a half-activated user out.
  useSessionWatchdog({
    cognitologin: props.cognitologin,
    navigate,
    isAuthPage,
  });

  // Track whether the previous render was an auth page so we can fade ONLY when
  // we cross the auth ↔ app boundary (login → dashboard or logout → login).
  // Other in-app navigations switch instantly with no transition.
  const previousIsAuthPage = useRef(isAuthPage);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Neural-network bloom transition.
  // A single boolean state flip tells NeuralNetworkBackground to start its
  // internal 0→1 animation. After that initial re-render, the animation runs
  // entirely inside the canvas rAF loop — no per-frame state updates.
  const [isNeuralTransitioning, setIsNeuralTransitioning] = useState(false);
  // Ambient "come alive" intensify — flipped on the instant login succeeds
  // (before the data load) so the network brightens as the login card fades,
  // and held through the wait until the bloom takes over.
  const [isNeuralActive, setIsNeuralActive] = useState(false);
  const transitionResolveRef = useRef<(() => void) | null>(null);

  // White overlay that covers the auth→app route swap. The dashboard renders
  // at full opacity underneath, then the overlay fades out to reveal it —
  // no flash, no CSSTransition crossfade.
  //   "none"   → no overlay
  //   "solid"  → overlay is opaque white (during bloom + route swap)
  //   "fading" → overlay is CSS-transitioning to transparent
  const [overlayPhase, setOverlayPhase] = useState<"none" | "solid" | "fading">("none");
  const overlayTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const OVERLAY_FADE_MS = 600;

  // When the overlay is handling the transition, disable the CSSTransition
  // crossfade so the dashboard renders instantly at full opacity underneath.
  const isOverlayActive = overlayPhase !== "none";
  const isCrossingAuthBoundary =
    previousIsAuthPage.current !== isAuthPage && !isOverlayActive;

  useEffect(() => {
    previousIsAuthPage.current = isAuthPage;
  }, [isAuthPage]);

  // Once the route has changed to a non-auth page and the overlay is solid,
  // start fading it out to reveal the dashboard underneath.
  const DASHBOARD_SETTLE_MS = 80;
  useEffect(() => {
    if (!isAuthPage && overlayPhase === "solid") {
      // Wait for the dashboard to fully render and any in-flight enrichment
      // queries (severity counts, sparklines, open vulns) to land. These
      // started ~1.2s before navigate, so 250ms is ample settle time.
      overlayTimerRef.current = setTimeout(() => {
        setOverlayPhase("fading");
        overlayTimerRef.current = setTimeout(() => {
          setOverlayPhase("none");
          setIsNeuralTransitioning(false);
          setIsNeuralActive(false);
        }, OVERLAY_FADE_MS);
      }, DASHBOARD_SETTLE_MS);
    }
  }, [isAuthPage, overlayPhase]);

  // Cleanup overlay timer on unmount
  useEffect(() => {
    return () => clearTimeout(overlayTimerRef.current);
  }, []);

  /** Flip the network into its intensified "loading" state. Called the instant
   *  login succeeds — before the data load — so the background comes alive as
   *  the login card fades, covering the wait until the bloom begins. */
  const startNeuralReveal = useCallback(() => {
    setIsNeuralActive(true);
  }, []);

  /** Start the bloom animation. Returns a Promise that resolves when the
   *  canvas animation reaches 1.0 (screen is white). */
  const startNeuralTransition = useCallback((): Promise<void> => {
    setIsNeuralTransitioning(true);
    // Overlay stays "none" during the bloom — the canvas paints the visual.
    // It only goes "solid" at the very end (in handleTransitionEnd) to bridge
    // the route swap without a flash.
    return new Promise((resolve) => {
      transitionResolveRef.current = resolve;
    });
  }, []);

  const handleTransitionEnd = useCallback(() => {
    // The canvas is now fully white. Place the solid overlay on top so it
    // persists through the route change (canvas unmounts when isAuthPage
    // flips). Wait one frame for the overlay to paint before resolving the
    // promise so Login's navigate() fires after the overlay is visible.
    setOverlayPhase("solid");
    requestAnimationFrame(() => {
      transitionResolveRef.current?.();
      transitionResolveRef.current = null;
    });
  }, []);

  return (
    <>
      {isAuthPage && (
        <NeuralNetworkBackground
          nodeCount={400}
          animationSpeed={1}
          opacity={0.15}
          active={isNeuralActive}
          transitioning={isNeuralTransitioning}
          onTransitionEnd={handleTransitionEnd}
        />
      )}
      {!isAuthPage && (
        <Menubar
          cognitologin={props.cognitologin}
          user={props.user}
          navigate={navigate}
        />
      )}

      {/*
        Route transitions:
          - The CSSTransition key is "auth" while on any auth page and "app"
            otherwise. So crossing the auth ↔ app boundary changes the key,
            triggering a real crossfade animation. In-app navigations don't
            change the key, so <Routes> swaps the matching route instantly with
            no animation.
          - We only apply the `fade` classNames when crossing the boundary so
            that any same-key re-renders don't accidentally fade.
      */}
      <TransitionGroup component={null}>
        <CSSTransition
          key={isAuthPage ? "auth" : "app"}
          classNames={isCrossingAuthBoundary ? "fade" : ""}
          timeout={isCrossingAuthBoundary ? { enter: 500, exit: 400 } : 0}
        >
          <div className="route-fade-wrapper">
          <Routes location={location}>
            <Route
              path={LOGIN_PAGE}
              element={
                <Login
                  cognitologin={props.cognitologin}
                  getUserDetails={props.getUserDetails}
                  setGraphDetails={props.setGraphDetails}
                  setUsername={props.setUsername}
                  navigate={navigate}
                  onRevealStart={startNeuralReveal}
                  onTransitionStart={startNeuralTransition}
                />
              }
            />
            <Route
              path={NEW_USER_LOGIN_PAGE}
              element={
                <Login
                  cognitologin={props.cognitologin}
                  getUserDetails={props.getUserDetails}
                  setGraphDetails={props.setGraphDetails}
                  setUsername={props.setUsername}
                  navigate={navigate}
                  onRevealStart={startNeuralReveal}
                  onTransitionStart={startNeuralTransition}
                  mode="activate"
                />
              }
            />
            <Route
              path={FORGOT_PASSWORD_PAGE}
              element={
                <ForgotPassword
                  cognitologin={props.cognitologin}
                  navigate={navigate}
                />
              }
            />
            <Route
              path={REQUEST_INVITE_PAGE}
              element={<RequestInvite navigate={navigate} />}
            />
            <Route
              path={RESET_PASSPHRASE_PAGE}
              element={
                <ResetPassword
                  location={location}
                  getUserDetails={props.getUserDetails}
                  cognitologin={props.cognitologin}
                  navigate={navigate}
                />
              }
            />
            {/* MFA challenge sits OUTSIDE PrivateRoute: the user has passed the
                password step but has no session yet, so isAuthenticated() is
                still false. Reuses the same bloom callbacks as Login so entering
                the app after the second factor matches the normal sign-in. */}
            <Route
              path={MFA_CHALLENGE_PAGE}
              element={
                <MfaChallenge
                  location={location}
                  cognitologin={props.cognitologin}
                  getUserDetails={props.getUserDetails}
                  navigate={navigate}
                  onRevealStart={startNeuralReveal}
                  onTransitionStart={startNeuralTransition}
                />
              }
            />
            <Route element={<PrivateRoute cognitologin={props.cognitologin} />}>
              <Route
                path={UPDATE_PASSPHRASE_PAGE}
                element={
                  <ChangePassword
                    getUserDetails={props.getUserDetails}
                    cognitologin={props.cognitologin}
                    navigate={navigate}
                  />
                }
              />
              <Route
                path={ACTIVATE_ACCOUNT_PAGE}
                element={
                  <ActivateAccount
                    username={props.username}
                    oldpassword={props.oldpassword}
                    getUserDetails={props.getUserDetails}
                    cognitologin={props.cognitologin}
                    navigate={navigate}
                  />
                }
              />
              <Route
                path={DASHBOARD_PAGE}
                element={
                  <Dashboard
                    user={props.user}
                    listOfWebapps={props.listOfWebapps}
                    isLoadingApps={props.dashboardListOfWebapps === null}
                    updateCurrentWebAppCallback={
                      props.updateCurrentWebAppCallback
                    }
                    navigate={navigate}
                  />
                }
              />
              {/*
                Admin-only routes. Wrapped in <RoleGate user={props.user}>
                so a Pentester URL-typing /manageusers, /addapplication,
                or /integrations is redirected to the Dashboard. Defense
                in depth — the backend resolvers enforce authz
                independently.
              */}
              <Route element={<RoleGate user={props.user} />}>
                <Route
                  path={MANAGE_USERS_PAGE}
                  element={
                    <Users
                      user={props.user}
                      cognitologin={props.cognitologin}
                      allUsers={props.allUsers}
                      listOfWebapps={props.listOfWebapps}
                      changeUserToAdminCallback={props.changeUserToAdminCallback}
                      navigate={navigate}
                    />
                  }
                />
                <Route
                  path={ADD_APP_PAGE}
                  element={
                    <NewApplication
                      subscriptionClient={props.subscriptionClient}
                      user={props.user}
                      cognitologin={props.cognitologin}
                      listOfWebapps={props.listOfWebapps}
                      updateListOfApps={props.updateListOfAppsCallback}
                      navigate={navigate}
                      updateCurrentWebAppCallback={
                        props.updateCurrentWebAppCallback
                      }
                    />
                  }
                />
                <Route
                  path={INTEGRATIONS_PAGE}
                  element={
                    <Integrations
                      user={props.user}
                      listOfWebapps={props.listOfWebapps}
                      isAdmin={props.user.isUserAdmin()}
                    />
                  }
                />
              </Route>
              {/* Account Owner only — Billing requires the singleton
                  CLIENT_OWNER_GROUP role. Per-app pentest budget lives on
                  the Applications rows + the WebApplication page, so there
                  is no separate tenant-wide Usage page. */}
              <Route element={<OwnerGate user={props.user} />}>
                <Route
                  path={BILLING_PAGE}
                  element={<BillingPage listOfWebapps={props.listOfWebapps} />}
                />
              </Route>
              <Route
                path={USER_PAGE}
                element={
                  <User
                    user={props.user}
                    cognitologin={props.cognitologin}
                    dashboardListOfWebapps={props.dashboardListOfWebapps}
                    updateCurrentWebAppCallback={
                      props.updateCurrentWebAppCallback
                    }
                    navigate={navigate}
                  />
                }
              />
              <Route
                path={MANAGE_APPS_PAGE}
                element={
                  <Applications
                    subscriptionClient={props.subscriptionClient}
                    user={props.user}
                    cognitologin={props.cognitologin}
                    listOfWebapps={props.listOfWebapps}
                    allUsers={props.allUsers}
                    updateListOfApps={props.updateListOfAppsCallback}
                    verifyApp={props.verifyApp}
                    updateCurrentWebAppCallback={
                      props.updateCurrentWebAppCallback
                    }
                    navigate={navigate}
                  />
                }
              />
              <Route
                path={VULNERABILITY_PAGE}
                element={
                  <Vulnerability
                    user={props.user}
                    cognitologin={props.cognitologin}
                    listOfWebapps={props.listOfWebapps}
                    webappid={props.currentWebAppId}
                  />
                }
              />
              <Route
                path={`${SINGLE_WEBAPP_PAGE}`}
                element={
                  <WebApplication
                    subscriptionClient={props.subscriptionClient}
                    user={props.user}
                    cognitologin={props.cognitologin}
                    listOfWebapps={props.listOfWebapps}
                    webappid={props.currentWebAppId}
                    verifyApp={props.verifyApp}
                    updateWebAppStatus={props.updateWebAppStatus}
                    updateWebAppTestState={props.updateWebAppTestState}
                    clientName={props.clientName}
                  />
                }
              />

              {/* Catch-all. Any signed-in path we do not serve lands on the
                  dashboard rather than an empty page. This is what a user gets
                  by trimming a URL to a parent that is not itself a route, e.g.
                  /account (only /account/billing exists). `replace` so Back
                  returns to where they actually came from instead of bouncing
                  off the unknown path again.

                  Placed inside PrivateRoute so an unknown path while signed out
                  still goes to login, not to a dashboard that would immediately
                  redirect. */}
              <Route path="*" element={<Navigate to={DASHBOARD_PAGE} replace />} />
            </Route>
          </Routes>
          </div>
        </CSSTransition>
      </TransitionGroup>

      {/* White overlay: sits on top of everything — including the Menubar
          (z-50) — during the auth→app route swap. The dashboard and Menubar
          render at full opacity underneath, then this fades out to reveal
          them together. z-[60] ensures nothing pokes through. */}
      {overlayPhase !== "none" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            backgroundColor: "#f9fafb",
            opacity: overlayPhase === "fading" ? 0 : 1,
            transition: `opacity ${OVERLAY_FADE_MS}ms ease-out`,
            pointerEvents: "none",
          }}
        />
      )}
    </>
  );
}
